import { get, post, put } from "@/api";
import type { AuthUser } from "@/utils/auth";

export interface LoginParams {
  account: string;
  password: string;
}

export interface LoginResult {
  member: AuthUser;
  token: string;
}

export interface LoginResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: LoginResult;
  errors?: Record<string, string[]>;
}

export interface MemberResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  /** 账号信息（含 is_canedit / is_divide） */
  data?: AuthUser;
  /** 当前待我审批节点的订单数量 */
  flows_count?: number;
  /** 待我审批的采购计划数量 */
  purchase_plans_count?: number;
  /** 与 data 同步，便于直接读取 */
  is_divide?: boolean;
  is_canedit?: boolean;
  is_purchase_plan_manager?: boolean;
}

/** 平台账号登录 */
export function login(params: LoginParams) {
  return post<LoginResponse>("/platform/auth/login", params);
}

/** 获取当前登录账号信息（并发去重 + 短缓存，避免布局多处重复请求） */
const MEMBER_CACHE_TTL_MS = 5_000;
let memberInflight: Promise<MemberResponse> | null = null;
let memberCache: { data: MemberResponse; at: number } | null = null;

export function invalidateMemberCache() {
  memberCache = null;
  memberInflight = null;
}

export function getMember(options?: { force?: boolean }) {
  const force = options?.force === true;
  const now = Date.now();

  if (!force && memberCache && now - memberCache.at < MEMBER_CACHE_TTL_MS) {
    return Promise.resolve(memberCache.data);
  }

  if (!force && memberInflight) {
    return memberInflight;
  }

  const request = get<MemberResponse>("/platform/auth/member")
    .then((res) => {
      memberCache = { data: res, at: Date.now() };
      return res;
    })
    .finally(() => {
      if (memberInflight === request) {
        memberInflight = null;
      }
    });

  memberInflight = request;
  return request;
}

export interface LogoutResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
}

/** 退出登录 */
export function logout() {
  invalidateMemberCache();
  return post<LogoutResponse>("/platform/auth/logout");
}

export interface ChangePasswordParams {
  /** 旧密码（后端同时用 password / old_password 校验） */
  old_password: string;
  /** 与旧密码同值，供后端「新旧不能相同」校验 */
  password: string;
  /** 新密码 */
  new_password: string;
}

export interface ChangePasswordResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
}

/** 修改登录密码 */
export function changePassword(params: ChangePasswordParams) {
  return put<ChangePasswordResponse>("/platform/auth/password", params);
}

export interface AuthDepartmentPerson {
  id: number | string;
  account?: string;
  name: string;
  phone?: string;
}

export interface AuthDepartment {
  id: number | string;
  title: string;
  /** 科室下人员（代购下单联动） */
  persons?: AuthDepartmentPerson[];
}

export interface AuthDepartmentsResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: AuthDepartment[];
}

/** 当前账号可筛选的科室列表 */
export function getAuthDepartments() {
  return get<AuthDepartmentsResponse>("/platform/auth/departments");
}
